import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Form, ListGroup, Modal, Spinner } from 'react-bootstrap';
import { api } from '../../utils/apiClient';
import './totals-panel.css';

const formatMoney = (value, currency = 'USD') => {
  const num = Number(value) || 0;
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(num);
  } catch {
    return `${currency} ${num.toFixed(2)}`;
  }
};

const currencySymbol = (currency = 'USD') => {
  try {
    const parts = new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
      currencyDisplay: 'narrowSymbol',
    }).formatToParts(0);
    const part = parts.find((p) => p.type === 'currency');
    return part ? part.value : currency;
  } catch {
    return currency;
  }
};

const TotalsPanel = ({
  currency = 'USD',
  itemCount = 0,
  listSubtotal = 0,
  subtotal = 0,
  lineDiscountAmount = 0,
  bulkDiscountPct = 0,
  discountAmount = 0,
  shipping = 0,
  shippingAdditional = 0,
  shippingMethodName = '',
  total = 0,
  ccEmails = '',
  editable = false,
  customer,
  lines = [],
  onChange,
}) => {
  const { t } = useTranslation();
  const [rateModalOpen, setRateModalOpen] = useState(false);
  const [ratesLoading, setRatesLoading] = useState(false);
  const [ratesError, setRatesError] = useState('');
  const [rates, setRates] = useState([]);

  const rateSelected = Boolean(String(shippingMethodName || '').trim());

  const handleField = (field, raw) => {
    if (!onChange) return;
    if (field === 'ccEmails') {
      onChange({ ccEmails: raw });
      return;
    }
    if (field === 'shippingAdditional') {
      const value = parseFloat(raw);
      onChange({ shippingAdditional: Number.isFinite(value) && value > 0 ? value : 0 });
      return;
    }
    if (field === 'bulkDiscountPct') {
      const value = parseFloat(raw);
      onChange({ bulkDiscountPct: Number.isFinite(value) ? value : 0 });
    }
  };

  const openGetRate = async () => {
    if (!onChange) return;

    const addr = customer?.address || {};
    const missing = [];
    if (!String(addr.zip || '').trim()) missing.push('zip');
    if (!String(addr.country || '').trim()) missing.push('country');
    if (missing.length) {
      setRateModalOpen(true);
      setRates([]);
      setRatesError(t('totals.getRateAddressRequired', { fields: missing.join(', ') }));
      return;
    }

    const hasLines = (lines || []).some(
      (line) => Boolean(line.productId) || Boolean(String(line.name || '').trim()),
    );
    if (!hasLines) {
      setRateModalOpen(true);
      setRates([]);
      setRatesError(t('totals.getRateProductsRequired'));
      return;
    }

    setRateModalOpen(true);
    setRatesLoading(true);
    setRatesError('');
    setRates([]);
    try {
      const res = await api.getShippingRates({
        customer: customer
          ? {
              name: customer.name,
              email: customer.email,
              company: customer.company,
              phone: customer.phone,
              address: customer.address,
            }
          : undefined,
        lines: (lines || []).map((line) => ({
          productId: line.productId,
          qty: line.qty,
          isCustom: line.isCustom,
          name: line.name,
          sku: line.sku,
          price: line.price,
          discountedPrice: line.discountedPrice,
          weight: line.weight,
        })),
      });
      setRates(Array.isArray(res.rates) ? res.rates : []);
      if (!res.rates?.length) {
        setRatesError(t('totals.getRateEmpty'));
      }
    } catch (err) {
      setRatesError(err?.message || t('totals.getRateFailed'));
    } finally {
      setRatesLoading(false);
    }
  };

  const selectRate = (rate) => {
    if (!onChange) return;
    onChange({
      shipping: Number.isFinite(rate.cost) ? rate.cost : 0,
      shippingMethodName: rate.name,
    });
    setRateModalOpen(false);
  };

  const clearShipping = () => {
    if (!onChange) return;
    onChange({ shipping: 0, shippingMethodName: '', shippingAdditional: 0 });
  };

  const showListSubtotal = lineDiscountAmount > 0 && listSubtotal > subtotal;

  return (
    <div className="q2c-totals row g-3">
      <div className="col-lg-7">
        <div className="q2c-totals-card">
          <div className="q2c-totals-card-head">
            <h6>{t('totals.quoteTotals')}</h6>
            <span className="q2c-totals-badge">{t('totals.itemsCount', { count: itemCount })}</span>
          </div>

          <div className="q2c-totals-rows">
            {showListSubtotal ? (
              <div className="q2c-totals-row">
                <span>{t('totals.listSubtotal')}</span>
                <strong>{formatMoney(listSubtotal, currency)}</strong>
              </div>
            ) : null}

            {showListSubtotal ? (
              <div className="q2c-totals-row">
                <span>{t('totals.lineDiscount')}</span>
                <strong className="q2c-totals-save">
                  −{formatMoney(lineDiscountAmount, currency)}
                </strong>
              </div>
            ) : null}

            <div className="q2c-totals-row">
              <span>{showListSubtotal ? t('totals.quotedSubtotal') : t('totals.subtotal')}</span>
              <strong>{formatMoney(subtotal, currency)}</strong>
            </div>

            <div className="q2c-totals-row q2c-totals-row--input">
              <span>{t('totals.bulkDiscount')}</span>
              {editable ? (
                <div className="q2c-totals-input">
                  <Form.Control
                    size="sm"
                    type="number"
                    min="0"
                    step="0.1"
                    value={bulkDiscountPct}
                    onChange={(e) => handleField('bulkDiscountPct', e.target.value)}
                    title={t('totals.bulkDiscountHelp')}
                  />
                  <em>%</em>
                </div>
              ) : (
                <strong>{bulkDiscountPct}%</strong>
              )}
            </div>

            <div className="q2c-totals-row">
              <span>{t('totals.discount')}</span>
              <strong className="q2c-totals-save">
                {discountAmount > 0 ? `−${formatMoney(discountAmount, currency)}` : formatMoney(0, currency)}
              </strong>
            </div>

            <div className="q2c-totals-row q2c-totals-row--input" id="q2c-cq-shipping-rate">
              <span>
                {t('totals.shippingRate')} <span className="text-danger">*</span>
              </span>
              {editable ? (
                <div className="q2c-totals-shipping">
                  <div className="q2c-totals-shipping-value" title={shippingMethodName || undefined}>
                    <strong>{rateSelected ? formatMoney(shipping, currency) : '—'}</strong>
                    {rateSelected ? (
                      <em className="q2c-totals-shipping-method">{shippingMethodName}</em>
                    ) : (
                      <em className="q2c-totals-shipping-method text-danger">
                        {t('totals.getRateRequired')}
                      </em>
                    )}
                  </div>
                  <Button size="sm" variant="outline-secondary" onClick={() => void openGetRate()}>
                    {t('totals.getRate')}
                  </Button>
                  {rateSelected ? (
                    <Button size="sm" variant="link" className="px-1" onClick={clearShipping}>
                      {t('totals.clearShipping')}
                    </Button>
                  ) : null}
                </div>
              ) : (
                <strong>
                  {formatMoney(shipping, currency)}
                  {shippingMethodName ? ` · ${shippingMethodName}` : ''}
                </strong>
              )}
            </div>

            {editable ? (
              <div className="q2c-totals-row q2c-totals-row--input">
                <span>{t('totals.shippingAdditional')}</span>
                <div className="q2c-totals-shipping">
                  <Form.Control
                    size="sm"
                    type="number"
                    min="0"
                    step="0.01"
                    disabled={!rateSelected}
                    value={shippingAdditional}
                    title={
                      rateSelected
                        ? t('totals.shippingAdditionalHelp')
                        : t('totals.shippingAdditionalDisabled')
                    }
                    onFocus={(e) => {
                      const input = e.currentTarget;
                      requestAnimationFrame(() => input.select());
                    }}
                    onChange={(e) => handleField('shippingAdditional', e.target.value)}
                  />
                </div>
              </div>
            ) : shippingAdditional > 0 ? (
              <div className="q2c-totals-row">
                <span>{t('totals.shippingAdditional')}</span>
                <strong>{formatMoney(shippingAdditional, currency)}</strong>
              </div>
            ) : null}
          </div>

          <div className="q2c-totals-grand">
            <span>{t('totals.quoteTotal')}</span>
            <strong>{formatMoney(total, currency)}</strong>
          </div>

          {editable ? (
            <Form.Group className="q2c-totals-cc">
              <Form.Label>{t('totals.ccEmails')}</Form.Label>
              <Form.Control
                placeholder={t('totals.ccEmailsPlaceholder')}
                value={ccEmails}
                onChange={(e) => handleField('ccEmails', e.target.value)}
              />
            </Form.Group>
          ) : null}
        </div>
      </div>

      {editable ? (
        <div className="col-lg-5">
          <div className="q2c-totals-help">
            <h6>{t('totals.howSendingWorks')}</h6>
            <ul>
              <li>
                <strong>{t('totals.saveDraftBold')}</strong> — {t('totals.saveDraftDesc')}
              </li>
              <li>
                <strong>{t('totals.emailPreviewBold')}</strong> — {t('totals.emailPreviewDesc')}
              </li>
              <li>
                <strong>{t('totals.sendQuoteBold')}</strong> — {t('totals.sendQuoteDesc')}
              </li>
            </ul>
            <p className="small text-muted mb-0 mt-2">{t('totals.shippingHelp')}</p>
          </div>
        </div>
      ) : null}

      <Modal show={rateModalOpen} onHide={() => setRateModalOpen(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{t('totals.selectShippingRate')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {ratesLoading ? (
            <div className="d-flex align-items-center gap-2 text-muted">
              <Spinner animation="border" size="sm" /> {t('totals.quotingRates')}
            </div>
          ) : null}
          {!ratesLoading && ratesError ? (
            <p className="text-danger mb-2 small">{ratesError}</p>
          ) : null}
          {!ratesLoading && rates.length ? (
            <>
              <p className="small text-muted mb-2">
                {t('totals.ratesForAddress')}
                {customer?.address?.city || customer?.address?.zip
                  ? ` (${[customer?.address?.city, customer?.address?.zip, customer?.address?.country]
                      .filter(Boolean)
                      .join(', ')})`
                  : ''}
                .
              </p>
              <ListGroup variant="flush" className="q2c-shipping-rate-list">
                {rates.map((rate) => (
                  <ListGroup.Item
                    key={rate.id}
                    action
                    className="d-flex justify-content-between align-items-start gap-3"
                    onClick={() => selectRate(rate)}
                  >
                    <div>
                      <div className="fw-semibold">{rate.name}</div>
                      {rate.type ? <div className="small text-muted">{rate.type}</div> : null}
                    </div>
                    <strong>{formatMoney(rate.cost, currency)}</strong>
                  </ListGroup.Item>
                ))}
              </ListGroup>
            </>
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setRateModalOpen(false)}>
            {t('common.cancel', { defaultValue: 'Cancel' })}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default TotalsPanel;
export { formatMoney, currencySymbol };
